/**
 * Fall-stage cartoon motion: pure functions of time / velocity that the Fall renderer turns into
 * sprite transforms (Pixi) or ctx transforms (Canvas 2D). Presentation only: they read physics
 * state and never write it; nothing here changes where a body is or how big its collider is.
 *
 * Vocabulary (docs/CARTOON_ART.md §4): squash/stretch keeps the volume (sx · sy = 1), pops
 * overshoot then settle, light stays upper left.
 */

function clamp(v, a, b) {
  return v < a ? a : v > b ? b : v;
}

/** Deterministic 0–1 hash of a number (per-body phases; never the game RNG). */
export function fallHash01(n) {
  var s = Math.sin((Number(n) || 0) * 127.1 + 311.7) * 43758.5453;
  return s - Math.floor(s);
}

/** How much longer than the config appear time the cartoon pop rings out (visual only). */
export var FALL_POP_DURATION_MUL = 1.6;

/**
 * Cartoon pop-in for a fresh spawn: grows from a speck past full size (≈1.19) and settles with
 * a squash that runs against the growth (tall while it shoots up, wide as it lands).
 * @param {number} k 0 → 1 over the pop
 * @returns {{ scale: number, sx: number, sy: number, alpha: number }}
 */
export function fallAppearPop(k) {
  if (!(k < 1)) return { scale: 1, sx: 1, sy: 1, alpha: 1 };
  var t = Math.max(0, k);
  var s = 1 - Math.exp(-5.5 * t) * Math.cos(10.5 * t);
  s = Math.max(0.12, s);
  var sq = 0.2 * Math.exp(-4.5 * t) * Math.sin(10.5 * t + 0.6);
  // Fade the ring-out of the squash to exactly 1 at the end of the pop.
  var tail = 1 - Math.pow(t, 6);
  sq *= tail;
  var sy = 1 + sq;
  return { scale: s, sx: 1 / sy, sy: sy, alpha: Math.min(1, t * 6) };
}

/**
 * Stretch along the velocity, keeping the volume: `e` (0 → max) is the extra length along
 * `dir`, the width shrinks by 1 / (1 + e).
 * @param {number} intensity 0–1 speed intensity (fallMotionTrailIntensity)
 * @param {number} vx
 * @param {number} vy
 * @param {number} [maxStretch] extra length at full speed (default 0.14)
 * @returns {{ e: number, dx: number, dy: number }}
 */
export function fallVelocityStretch(intensity, vx, vy, maxStretch) {
  var m = maxStretch != null ? maxStretch : 0.14;
  var speed = Math.sqrt((vx || 0) * (vx || 0) + (vy || 0) * (vy || 0));
  var k = clamp(Number(intensity) || 0, 0, 1);
  if (!(speed > 1e-6) || !(k > 0) || !(m > 0)) return { e: 0, dx: 0, dy: 1 };
  // Ease in so a gentle drift barely deforms and a real fall reads clearly.
  return { e: m * k * (2 - k), dx: vx / speed, dy: vy / speed };
}

/**
 * Axis-aligned scale (sx, sy) approximating a stretch `e` along (dx, dy) in a frame turned by
 * `angle` (0 = screen axes). A sprite can only scale along its own axes, so a body spinning at
 * 45° to its fall gets the stretch spread over both axes (≈ no stretch) rather than a wrong one.
 * sx · sy = 1 always.
 * @returns {{ sx: number, sy: number }}
 */
export function stretchAxes(st, angle) {
  if (!st || !(st.e > 0)) return { sx: 1, sy: 1 };
  var a = angle || 0;
  var ca = Math.cos(a), sa = Math.sin(a);
  // Direction in the sprite's local frame.
  var lx = st.dx * ca + st.dy * sa;
  var ly = -st.dx * sa + st.dy * ca;
  var L = Math.log(1 + st.e);
  var d = lx * lx - ly * ly;
  return { sx: Math.exp(L * d), sy: Math.exp(-L * d) };
}

/**
 * Creature blinks: every few seconds, phase and rhythm hashed from the body id, sometimes a
 * quick double blink. Driven by the pause-aware anim clock so a paused frame repeats exactly.
 * @param {number} id body id
 * @param {number} tMs anim time (ms)
 * @returns {boolean} eyes closed
 */
export function fallBlinkClosed(id, tMs) {
  if (tMs == null || !isFinite(tMs)) return false;
  var h = fallHash01(id);
  var period = 2400 + h * 2400;
  var local = (tMs + fallHash01(id * 7.13 + 1) * period) % period;
  if (local < 0) local += period;
  if (local < 120) return true;
  return fallHash01(id * 3.7 + 2) < 0.35 && local > 230 && local < 330;
}

/**
 * Catcher flask squash about its base from the juice springs: `hit` (the "flask" spring the
 * juice kicks on boulder hits, ≈1 then ringing) squashes it down and makes it bulge, `gulp`
 * (a smaller spring kicked when something is swallowed) a little swallow.
 * @param {number} hit
 * @param {number} gulp
 * @returns {{ sx: number, sy: number }}
 */
export function catcherFlaskSquash(hit, gulp) {
  var v = clamp((Number(hit) || 0) * 0.13 + (Number(gulp) || 0) * 0.075, -0.24, 0.24);
  var sy = 1 - v;
  return { sx: 1 / sy, sy: sy };
}

/**
 * The bubble shield's scale and alpha from the player's shield tween ({ kind, t0 }) instead of
 * the old linear grow/fade: "in" pops from small past full size and wobbles to rest; "out"
 * swells for a beat, then bursts (gone by 45% of the tween; the caller adds the burst FX).
 * @param {{ kind: string, t0: number }|null} fx
 * @param {number} t anim time (ms)
 * @param {{ appearMs?: number, disappearMs?: number }} [cfg]
 * @returns {{ scale: number, alpha: number, wobble: number, popped: boolean, phase: string }}
 */
export function shieldBubbleLook(fx, t, cfg) {
  var c = cfg || {};
  var appearMs = c.appearMs > 0 ? c.appearMs : 280;
  var outMs = c.disappearMs > 0 ? c.disappearMs : 220;
  if (fx && fx.kind === "in") {
    var p = clamp((t - fx.t0) / (appearMs * 1.6), 0, 1);
    // The ring-out is eased to exactly 1 at the end, so the hand-off to the steady bubble is seamless.
    var s = 1 - Math.exp(-5 * p) * Math.cos(9.5 * p) * (1 - p * p * p * p);
    return {
      scale: Math.max(0.2, s),
      alpha: Math.min(1, p * 5),
      wobble: 0.08 * Math.exp(-3 * p) * Math.sin(p * 14) * (1 - p * p * p * p),
      popped: false,
      phase: "in",
    };
  }
  if (fx && fx.kind === "out") {
    var q = clamp((t - fx.t0) / outMs, 0, 1);
    var swell = 1 - Math.pow(1 - Math.min(1, q / 0.35), 3);
    if (q < 0.35) {
      return { scale: 1 + 0.12 * swell, alpha: 1, wobble: 0.06 * Math.sin(q * 40), popped: false, phase: "swell" };
    }
    var k = Math.min(1, (q - 0.35) / 0.1);
    return { scale: 1.12 + 0.1 * k, alpha: 1 - k, wobble: 0, popped: true, phase: "pop" };
  }
  return { scale: 1, alpha: 1, wobble: 0, popped: false, phase: "on" };
}

/**
 * Countdown digit pop for the second in progress (`u` = 0 at the tick → 1 at the next): springs
 * in tall-then-wide with overshoot, rests, then zips away (narrow and tall, fading).
 * @param {number} u 0–1
 * @returns {{ sx: number, sy: number, alpha: number }}
 */
export function countdownDigitPop(u) {
  var t = clamp(u, 0, 1);
  var s = 1 - Math.exp(-9 * t) * Math.cos(16 * t);
  s = Math.max(0.05, s);
  var sq = 0.22 * Math.exp(-7 * t) * Math.sin(16 * t + 0.5);
  var sy = s * (1 + sq);
  var sx = s / (1 + sq);
  var alpha = Math.min(1, t / 0.05);
  if (t > 0.84) {
    var k = (t - 0.84) / 0.16;
    var e = k * k;
    sx *= 1 - 0.75 * e;
    sy *= 1 + 0.35 * e;
    alpha *= 1 - e;
  }
  return { sx: sx, sy: sy, alpha: clamp(alpha, 0, 1) };
}

/**
 * Edge-hazard marker motion: a hop every ~0.55 s (up, then a squash on landing).
 * @param {number} tMs anim time
 * @param {number} sinceMs how long the marker has been up (for its pop-in)
 * @returns {{ lift: number, sx: number, sy: number, scale: number }} lift 0–1 of a hop height
 */
export function warnMarkerHop(tMs, sinceMs) {
  var period = 560;
  var ph = (((tMs || 0) % period) + period) % period / period;
  var lift = Math.sin(ph * Math.PI);
  lift = lift * lift;
  // Landing squash right after touch-down (ph near 0).
  var land = ph < 0.18 ? Math.sin((ph / 0.18) * Math.PI) : 0;
  var sy = 1 - 0.16 * land + 0.06 * lift;
  var k = clamp((sinceMs || 0) / 320, 0, 1);
  var pop = k >= 1 ? 1 : Math.max(0.1, 1 - Math.exp(-6 * k) * Math.cos(11 * k));
  return { lift: lift, sx: 1 / sy, sy: sy, scale: pop };
}

/**
 * Flask shard spin and fade-out: shards spin faster than the burst data says and shrink away
 * near the end of a short life instead of falling off screen at full size.
 * @param {number} ageMs since the burst
 * @param {number} seed per-shard 0–1
 * @returns {{ spinMul: number, scale: number, alpha: number, gone: boolean }}
 */
export function shardLifeLook(ageMs, seed) {
  var life = 780 + (seed || 0) * 420;
  var fade = 260;
  var a = Math.max(0, ageMs || 0);
  if (a >= life) return { spinMul: 2.4, scale: 0, alpha: 0, gone: true };
  var k = a > life - fade ? (a - (life - fade)) / fade : 0;
  return { spinMul: 2.4, scale: 1 - k * k * 0.85, alpha: 1 - k * k, gone: false };
}
